/**
 * SoulumoAccountPage · Feb 2026
 * ───────────────────────────────────────────────────────────────────
 * The Account page shown on `soulumo.app`. Keeps the user strictly
 * inside Soulumo territory — no Reclaim logo, no Programs/Challenges
 * nav, no "Share your journey", no Circles, no Reclaim onboarding.
 *
 * Rendered by `AccountPage` when `isBrandedNarrow(resolveBrand()) &&
 * brand === "soulumo"`.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import SoulumoHeader from "../../components/SoulumoHeader";
import SoulumoFooter from "../../components/SoulumoFooter";
import BetaPulseModal from "../../components/BetaPulseModal";
import {
  Crown, Star, CreditCard, HelpCircle, LogOut, Settings, Mic,
  ChevronRight, Pencil, MessageSquare, Library as LibraryIcon,
  Sparkles, Handshake,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const TOK = {
  cream: "#f7efdd",
  ink: "#0d1526",
  slate: "#2a334d",
  gold: "#d99b6b",
  goldSoft: "#e0a97a",
  mist: "rgba(5,11,26,0.55)",
};

export default function SoulumoAccountPage() {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showVoiceClone, setShowVoiceClone] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/partner/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } })
      .then((r) => setIsPartner(!!r.data.is_partner)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ name });
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };


  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(180deg, #0a1226 0%, #050a17 100%)",
        color: TOK.cream,
      }}
      data-testid="soulumo-account-page"
    >
      <SoulumoHeader />

      <div className="max-w-md mx-auto px-6 pb-28 pt-6 space-y-5">
        {/* Title */}
        <header className="pt-2">
          <p
            className="uppercase"
            style={{
              color: "rgba(224,169,122,0.86)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
              letterSpacing: "0.36em",
            }}
          >
            ✦ Soulumo · your account ✦
          </p>
          <h1
            className="mt-2 italic font-light"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(2rem, 7vw, 2.5rem)",
              lineHeight: 1.15,
            }}
          >
            Welcome, {(user?.name || "friend").split(" ")[0]}.
          </h1>
          <p
            className="mt-1 italic"
            style={{
              color: "rgba(247,239,221,0.6)",
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "1rem",
            }}
          >
            Quietly manage your Soulumo profile.
          </p>
        </header>

        {/* Profile card */}
        <SoulumoCard>
          <div className="flex items-center gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(180deg,#f0c790,#d99b6b)",
                color: "#1a0f05",
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.5rem",
                fontWeight: 600,
              }}
            >
              {(user?.name || "F").trim()[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              {editing ? (
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="soulumo-account-name-input"
                  style={{
                    width: "100%",
                    background: "rgba(0,0,0,0.35)",
                    border: `1px solid rgba(224,169,122,0.22)`,
                    color: TOK.cream,
                    borderRadius: 10,
                    padding: "8px 12px",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.95rem",
                    outline: "none",
                  }}
                />
              ) : (
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "1.3rem",
                    color: TOK.cream,
                  }}
                  data-testid="soulumo-account-name"
                >
                  {user?.name || "Friend"}
                </div>
              )}
              <div
                className="truncate"
                style={{
                  color: "rgba(247,239,221,0.55)",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.85rem",
                }}
              >
                {user?.email}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            {!editing ? (
              <button
                type="button"
                onClick={() => { setEditing(true); setName(user?.name || ""); }}
                data-testid="soulumo-account-edit"
                className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-full"
                style={{
                  background: "transparent",
                  border: `1px solid rgba(224,169,122,0.32)`,
                  color: TOK.goldSoft,
                  fontFamily: '"Manrope", sans-serif',
                }}
              >
                <Pencil size={14} /> Edit profile
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="soulumo-account-save"
                  className="text-sm px-4 py-2 rounded-full disabled:opacity-60"
                  style={{
                    background: "linear-gradient(180deg,#f0c790,#d99b6b)",
                    color: "#1a0f05",
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="text-sm px-4 py-2 rounded-full italic"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "rgba(247,239,221,0.55)",
                    fontFamily: '"Cormorant Garamond", serif',
                  }}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </SoulumoCard>

        {/* Soulumo-only links */}
        <SoulumoCard noPad>
          <AccountLink
            icon={<LibraryIcon size={18} />}
            label="My Library"
            sub="Your saved installations & words"
            onClick={() => navigate("/my-library")}
            testid="soulumo-account-link-library"
          />
          <Divider />
          {isPartner && (<>
            <AccountLink
              icon={<Handshake size={18} />}
              label="My partner area"
              sub="Clicks, sign-ups, starter kit & earnings"
              onClick={() => navigate("/soulumo/partner")}
              testid="soulumo-account-link-partner"
            />
            <Divider />
          </>)}
          <AccountLink
            icon={<Sparkles size={18} />}
            label="My Installations"
            sub="Everything you've built"
            onClick={() => navigate("/my-installations")}
            testid="soulumo-account-link-installations"
          />
          <Divider />
          <AccountLink
            icon={<Mic size={18} />}
            label="My voice"
            sub="Your own voice in every installation"
            onClick={() => navigate("/voice-clone")}
            testid="soulumo-account-link-voice"
          />
          <Divider />
          <AccountLink
            icon={<MessageSquare size={18} />}
            label="Share feedback"
            sub="A quick check-in with Michael"
            onClick={() => setShowFeedback(true)}
            testid="soulumo-account-link-feedback"
          />
          <Divider />
          <AccountLink
            icon={<HelpCircle size={18} />}
            label="Support"
            sub="hello@soulumo.app"
            onClick={() => { window.location.href = "mailto:hello@soulumo.app"; }}
            testid="soulumo-account-link-support"
          />
        </SoulumoCard>

        {/* Admin */}
        {user?.role === "admin" && (
          <SoulumoCard noPad>
            <AccountLink
              icon={<Settings size={18} />}
              label="Admin dashboard"
              sub="Beta intelligence, reservations, voices"
              onClick={() => navigate("/admin")}
              testid="soulumo-account-link-admin"
            />
          </SoulumoCard>
        )}

        {/* Logout */}
        <button
          type="button"
          onClick={handleLogout}
          data-testid="soulumo-account-logout"
          className="w-full py-3 rounded-full italic"
          style={{
            background: "transparent",
            border: "1px solid rgba(248,113,113,0.4)",
            color: "#fca5a5",
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.95rem",
            cursor: "pointer",
          }}
        >
          <LogOut size={16} style={{ display: "inline", verticalAlign: "-3px", marginRight: 8 }} />
          Sign out
        </button>

        <p
          className="text-center italic pt-2"
          style={{
            color: "rgba(247,239,221,0.35)",
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "0.82rem",
          }}
        >
          Made with care · Soulumo
        </p>
      </div>

      <SoulumoFooter />

      <BetaPulseModal
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
        source="footer-link"
      />

      {showVoiceClone && (
        <VoiceCloneInterestModal onClose={() => setShowVoiceClone(false)} />
      )}
    </div>
  );
}

// ── Soulumo-styled card
function SoulumoCard({ children, noPad = false }) {
  return (
    <div
      className="rounded-2xl"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(224,169,122,0.18)",
        padding: noPad ? 0 : 20,
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      }}
    >
      {children}
    </div>
  );
}

function AccountLink({ icon, label, sub, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full px-4 py-4 flex items-center gap-3 transition-colors"
      style={{
        background: "transparent",
        border: "none",
        color: TOK.cream,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: "rgba(224,169,122,0.14)",
          color: TOK.goldSoft,
        }}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className="block"
          style={{
            color: TOK.cream,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.98rem",
            fontWeight: 500,
          }}
        >
          {label}
        </span>
        <span
          className="block truncate"
          style={{
            color: "rgba(247,239,221,0.52)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
          }}
        >
          {sub}
        </span>
      </span>
      <ChevronRight size={18} style={{ color: "rgba(247,239,221,0.4)" }} />
    </button>
  );
}

function Divider() {
  return <div style={{ height: 1, background: "rgba(224,169,122,0.12)" }} />;
}

// ── Clone-my-voice demand signal modal — opened from the account
//    page. Checks `/api/voice-clone/interest/me` to avoid double-
//    asking, posts to `/api/voice-clone/interest` on opt-in.
function VoiceCloneInterestModal({ onClose }) {
  const [state, setState] = useState({ loading: true, signedUp: false });
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    (async () => {
      try {
        const token = localStorage.getItem("reclaim_token");
        const r = await fetch(`${API}/api/voice-clone/interest/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = r.ok ? await r.json() : { signed_up: false };
        setState({ loading: false, signedUp: !!d.signed_up });
      } catch {
        setState({ loading: false, signedUp: false });
      }
    })();
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(""); setSubmitting(true);
    try {
      const token = localStorage.getItem("reclaim_token");
      const r = await fetch(`${API}/api/voice-clone/interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: note.trim(), source: "account-page" }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDone(true);
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally { setSubmitting(false); }
  };

  const alreadySignedUp = state.signedUp || done;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="voice-clone-interest-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 sm:p-7"
        style={{
          background: "linear-gradient(180deg, rgba(10,20,40,0.98) 0%, rgba(5,11,26,1) 100%)",
          border: "1px solid rgba(224,169,122,0.3)",
          color: TOK.cream,
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 30px 90px rgba(0,0,0,0.55), 0 0 60px rgba(224,169,122,0.08)",
        }}
      >
        <div
          className="uppercase"
          style={{
            color: "rgba(224,169,122,0.86)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.58rem",
            letterSpacing: "0.36em",
          }}
        >
          ✦ Premium add-on · coming soon
        </div>
        <h3
          className="mt-2 italic font-light"
          style={{
            color: TOK.cream,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(1.5rem, 5vw, 1.9rem)",
            lineHeight: 1.2,
          }}
        >
          Your own voice.
        </h3>

        {alreadySignedUp ? (
          <div data-testid="voice-clone-interest-done">
            <p className="mt-3 italic"
              style={{ color: "rgba(247,239,221,0.72)", fontFamily: '"Cormorant Garamond", serif', fontSize: "1rem", lineHeight: 1.6 }}>
              You&apos;re on the list for <strong>My voice</strong>.
              Michael will reach out personally when it&apos;s ready.
              Thank you for the signal.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="voice-clone-interest-close"
              className="mt-6 w-full rounded-full py-3"
              style={{
                background: "transparent",
                border: "1px solid rgba(224,169,122,0.6)",
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.76rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p className="mt-3 italic"
              style={{ color: "rgba(247,239,221,0.72)", fontFamily: '"Cormorant Garamond", serif', fontSize: "1rem", lineHeight: 1.55 }}>
              Record a 90-second sample once. Then every new installation
              speaks your words in <em>your</em> voice.
            </p>

            <div
              className="mt-5 rounded-2xl p-4"
              style={{
                background: "rgba(224,169,122,0.08)",
                border: "1px solid rgba(224,169,122,0.22)",
              }}
            >
              <div style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.05rem",
                color: TOK.cream,
                fontStyle: "italic",
              }}>
                <s className="mr-1 opacity-60" style={{ fontStyle: "normal" }} data-testid="account-was-price">£9.99</s><strong style={{ color: TOK.goldSoft, fontStyle: "normal" }}>£6.99 / month</strong> or £59.99 / year
                {" "}when it launches
              </div>
              <div style={{
                color: "rgba(247,239,221,0.55)",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.8rem",
                marginTop: 4,
              }}>
                No charge today — just tell Michael you&apos;d use it.
              </div>
            </div>

            <form onSubmit={submit} className="mt-5" data-testid="voice-clone-interest-form">
              <label
                className="block uppercase mb-1.5"
                style={{
                  color: "rgba(247,239,221,0.44)",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.6rem",
                  letterSpacing: "0.28em",
                }}
              >
                Anything you want me to know? (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="e.g. For my mum, who struggles to sleep."
                data-testid="voice-clone-interest-note"
                style={{
                  width: "100%",
                  background: "rgba(0,0,0,0.35)",
                  border: "1px solid rgba(224,169,122,0.22)",
                  color: TOK.cream,
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.92rem",
                  resize: "vertical",
                  minHeight: 84,
                  outline: "none",
                }}
              />
              {err && (
                <div className="mt-3 text-sm italic"
                  style={{ color: "#f0a0a0", fontFamily: '"Cormorant Garamond", serif' }}>
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                data-testid="voice-clone-interest-submit"
                className="mt-4 w-full rounded-full py-3 disabled:opacity-60"
                style={{
                  background: "linear-gradient(180deg,#f0c790,#d99b6b)",
                  color: "#1a0f05",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.76rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  border: "1px solid #f5e0c5",
                  minHeight: 46,
                  cursor: submitting ? "wait" : "pointer",
                }}
              >
                {submitting ? "Sending…" : "Count me in"}
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="voice-clone-interest-cancel"
                className="mt-2 w-full py-2 italic"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "rgba(247,239,221,0.55)",
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "0.9rem",
                  cursor: "pointer",
                }}
              >
                Not now
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
